import { useState, useEffect, useCallback, useRef, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type DragEvent, type ClipboardEvent, type ChangeEvent } from 'react';
import { useAuth } from '../auth';
import { MarkdownEditor } from '../components/MarkdownEditor';
import {
  fetchTree,
  fetchFile,
  saveFile,
  createFile,
  deleteFile,
  renameFile,
  searchContent,
  moveFile,
  fetchHistory,
  chatSkillBuilder,
  type TreeNode,
  type FileData,
  type HistoryEntry,
  type SkillBuilderResponse,
} from '../api';
import { frontmatterInicialSkill } from '@flux/shared';
import './Studio.css';

// ── Types ──


type SaveStatus = 'saved' | 'modified' | 'saving' | 'readonly';

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  changes?: SkillBuilderResponse['changes'];
  /** Tags shown for user messages that had attachments */
  attachmentTags?: string[];
}

// ── Main Studio Component ──

export function Studio() {
  const { user, logout } = useAuth();

  const [tree, setTree] = useState<TreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [contentMatchKeys, setContentMatchKeys] = useState<Set<number> | null>(null);

  const [selectedFile, setSelectedFile] = useState<FileData | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [editorVersion, setEditorVersion] = useState(0);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [error, setError] = useState<string | null>(null);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // File upload state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Snapshot of the content that was sent to the backend with the last chat message.
  // The 'before' text in AI responses is extracted from this exact content,
  // so matching against it is guaranteed to work.
  const sentContentRef = useRef('');

  const chatEndRef = useRef<HTMLDivElement>(null);

  // ── Modal state ──
  const [createModal, setCreateModal] = useState<{ parentKey: number; parentName: string } | null>(null);
  const [createFileName, setCreateFileName] = useState('');
  const [deleteModal, setDeleteModal] = useState<{ key: number; name: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── History ──
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Resizable panels ──
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [builderWidth, setBuilderWidth] = useState(320);
  const draggingRef = useRef<'sidebar' | 'builder' | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  useEffect(() => {
    const onMouseMove = (e: globalThis.MouseEvent) => {
      if (!draggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      if (draggingRef.current === 'sidebar') {
        setSidebarWidth(Math.max(180, Math.min(600, startWidthRef.current + delta)));
      } else {
        // Builder: drag left = bigger, so subtract delta
        setBuilderWidth(Math.max(200, Math.min(600, startWidthRef.current - delta)));
      }
    };
    const onMouseUp = () => {
      draggingRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startDrag = (panel: 'sidebar' | 'builder', e: ReactMouseEvent) => {
    e.preventDefault();
    draggingRef.current = panel;
    startXRef.current = e.clientX;
    startWidthRef.current = panel === 'sidebar' ? sidebarWidth : builderWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // ── Load tree on mount ──
  useEffect(() => {
    loadTree();
  }, []);

  const loadTree = async () => {
    setTreeLoading(true);
    try {
      const data = await fetchTree();
      setTree(data);
    } catch (err) {
      setError(`Erro ao carregar árvore: ${err instanceof Error ? err.message : err}`);
    } finally {
      setTreeLoading(false);
    }
  };

  // ── File operations ──
  const openFile = async (node: TreeNode) => {
    if (node.type !== 'file') return;
    try {
      // Save current file before switching (await to prevent race condition)
      if (selectedFile && saveStatus === 'modified' && selectedFile.ikey > 0) {
        await saveFile(selectedFile.ikey, editorContentRef.current);
      }
      const data = await fetchFile(node.key);
      setSelectedFile(data);
      setEditorContent(data.content);
      setEditorVersion(v => v + 1);
      setSaveStatus(data.editable ? 'saved' : 'readonly');

      setChatMessages([]);
      setHistoryOpen(false);
      setHistoryEntries([]);
    } catch (err) {
      setError(`Erro ao abrir arquivo: ${err instanceof Error ? err.message : err}`);
    }
  };

  // ── Create file ──
  const handleCreateFile = async () => {
    if (!createModal || !createFileName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    const name = createFileName.trim();
    const fileName = name.endsWith('.md') ? name : `${name}.md`;
    const baseName = fileName.replace(/\.md$/, '');
    // Arquivo de apoio (skill~pasta~arquivo.md) não leva cabeçalho de skill (ADR-070)
    const frontmatter = fileName.includes('~') ? '' : frontmatterInicialSkill(baseName);

    try {
      await createFile(createModal.parentKey, fileName, frontmatter);
      setCreateModal(null);
      setCreateFileName('');
      await loadTree();
    } catch (err) {
      setError(`Erro ao criar arquivo: ${err instanceof Error ? err.message : err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Delete file ──
  const handleDeleteFile = async () => {
    if (!deleteModal || isSubmitting) return;
    if (deleteConfirm !== deleteModal.name) return;
    setIsSubmitting(true);

    try {
      await deleteFile(deleteModal.key);
      if (selectedFile?.ikey === deleteModal.key) {
        setSelectedFile(null);
        setEditorContent('');
        setEditorVersion(v => v + 1);
      }
      setDeleteModal(null);
      setDeleteConfirm('');
      await loadTree();
    } catch (err) {
      setError(`Erro ao deletar: ${err instanceof Error ? err.message : err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track latest content via ref to avoid stale closures in handleSave
  const editorContentRef = useRef(editorContent);
  useEffect(() => {
    editorContentRef.current = editorContent;
  }, [editorContent]);

  const handleSave = useCallback(async () => {
    if (!selectedFile || saveStatus === 'readonly') return;
    if (selectedFile.ikey < 0) {
      setError('⛔ Registro de chave negativa — protegido pelo sistema. Para alterações, entre em contato com a área de TIN.');
      return;
    }
    setSaveStatus('saving');
    try {
      const res = await saveFile(selectedFile.ikey, editorContentRef.current);
      setSaveStatus('saved');

      // Auto-rename: update tree node and selectedFile in-place
      if (res.renamed) {
        setSelectedFile(prev => prev ? { ...prev, name: res.renamed! } : prev);
        setTree(prev => {
          const updateNode = (nodes: TreeNode[]): TreeNode[] =>
            nodes.map(n => {
              if (n.key === selectedFile.ikey) return { ...n, name: res.renamed! };
              if (n.children) return { ...n, children: updateNode(n.children) };
              return n;
            });
          return updateNode(prev);
        });
      }
    } catch (err) {
      setError(`Erro ao salvar: ${err instanceof Error ? err.message : err}`);
      setSaveStatus('modified');
    }
  }, [selectedFile, saveStatus]);

  // ── Ctrl+S ──
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  // ── Auto-save with 5s debounce ──
  useEffect(() => {
    if (saveStatus !== 'modified') return;
    const timer = setTimeout(() => {
      handleSave();
    }, 5000);
    return () => clearTimeout(timer);
  }, [editorContent, saveStatus, handleSave]);

  // ── Warn on unsaved exit ──
  useEffect(() => {
    if (saveStatus !== 'modified') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveStatus]);

  // ── Content search with debounce ──
  useEffect(() => {
    if (searchFilter.trim().length < 2) {
      setContentMatchKeys(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await searchContent(searchFilter.trim());
        setContentMatchKeys(new Set(result.keys));
      } catch {
        setContentMatchKeys(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchFilter]);

  // ── Content change tracking ──
  const handleContentChange = (newContent: string) => {
    setEditorContent(newContent);
    if (saveStatus !== 'readonly') {
      setSaveStatus('modified');
    }
  };

  // ── Skill Builder ──

  /** Collect all file ikeys from the tree, excluding the current file */
  const collectFileKeys = (nodes: TreeNode[]): number[] => {
    const keys: number[] = [];
    const walk = (ns: TreeNode[]) => {
      for (const n of ns) {
        if (n.type === 'file' && n.key !== selectedFile?.ikey) keys.push(n.key);
        if (n.children) walk(n.children);
      }
    };
    walk(nodes);
    return keys;
  };

  const sendChatMessage = async () => {
    if ((!chatInput.trim() && pendingFiles.length === 0) || chatLoading) return;
    const prompt = chatInput.trim() || (pendingFiles.length > 0 ? 'Analise os arquivos enviados.' : '');
    const filesToSend = [...pendingFiles];
    setChatInput('');
    setPendingFiles([]);

    // Build attachment tags for the user message
    const attachmentTags: string[] = [];
    const imgCount = filesToSend.filter(f => f.type.startsWith('image/')).length;
    const emlFiles = filesToSend.filter(f => f.name.toLowerCase().endsWith('.eml'));
    if (imgCount > 0) attachmentTags.push(`📷 ${imgCount} imagem(ns)`);
    for (const eml of emlFiles) attachmentTags.push(`📧 ${eml.name}`);

    setChatMessages((prev) => [...prev, { role: 'user', text: prompt, attachmentTags }]);
    setChatLoading(true);

    try {
      const otherSkillKeys = collectFileKeys(tree);
      const res = await chatSkillBuilder(
        prompt,
        editorContent || undefined,
        selectedFile?.path,
        selectedFile ? 'edit' : 'create',
        chatMessages.map(m => ({ role: m.role, text: m.text })),
        otherSkillKeys,
        filesToSend.length > 0 ? filesToSend : undefined,
      );
      // Store the snapshot the backend used for before-text extraction
      if (res.snapshot !== undefined) {
        sentContentRef.current = res.snapshot;
      }
      setChatMessages((prev) => [
        ...prev,
        { role: 'ai', text: res.explanation || res.markdown, changes: res.changes },
      ]);
    } catch (err) {
      setChatMessages((prev) => [
        ...prev,
        { role: 'ai', text: `❌ Erro: ${err instanceof Error ? err.message : err}` },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // ── File upload handlers ──

  const acceptedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'message/rfc822'];
  const acceptedExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.eml'];

  const isAcceptedFile = (file: File): boolean => {
    if (acceptedTypes.includes(file.type)) return true;
    return acceptedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  const addFiles = (files: FileList | File[]) => {
    const newFiles = Array.from(files).filter(isAcceptedFile);
    if (newFiles.length === 0) return;
    setPendingFiles(prev => [...prev, ...newFiles].slice(0, 5)); // max 5
  };

  const removeFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFilePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const handleBuilderDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  };

  const handleBuilderDragLeave = (e: DragEvent<HTMLDivElement>) => {
    // Only set false if leaving the builder entirely
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
      setDragOver(false);
    }
  };

  const handleBuilderDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = ''; // reset so same file can be selected again
    }
  };

  const acceptChange = (change: NonNullable<SkillBuilderResponse['changes']>[0]) => {
    // Work against the snapshot (the content that was sent to the backend).
    // The 'before' was extracted from it, so indexOf is guaranteed to find it.
    const snapshot = sentContentRef.current;
    const idx = snapshot.indexOf(change.before);

    if (idx === -1) {
      console.warn('[SkillBuilder] before not found in snapshot.',
        '\nBefore:', JSON.stringify(change.before.substring(0, 200)),
        '\nSnapshot:', JSON.stringify(snapshot.substring(0, 200)),
      );
      setError('⚠️ Texto não encontrado. Tente enviar uma nova mensagem no chat.');
      return;
    }

    const newContent = snapshot.substring(0, idx) + change.after + snapshot.substring(idx + change.before.length);

    // Update everything: editor, ref, AND snapshot (so subsequent accepts work)
    sentContentRef.current = newContent;
    setEditorContent(newContent);
    editorContentRef.current = newContent;
    setEditorVersion(v => v + 1);
    if (saveStatus !== 'readonly') {
      setSaveStatus('modified');
    }
  };

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // ── Status label ──
  const statusLabel: Record<SaveStatus, string> = {
    saved: 'Salvo ✓',
    modified: 'Modificado ●',
    saving: 'Salvando...',
    readonly: '🔒 Somente leitura',
  };

  if (!user) {
    window.location.href = '/';
    return null;
  }

  return (
    <div className="studio">
      {/* Top Bar */}
      <header className="studio__topbar">
        <a href="/" className="studio__topbar-brand">
          <span className="studio__topbar-icon">↻</span>
          FLUX Studio
        </a>
        <div className="studio__topbar-spacer" />
        <div className="studio__topbar-user">
          <span className="studio__topbar-avatar">
            {user.name.charAt(0).toUpperCase()}
          </span>
          {user.name}
          <button className="studio__topbar-logout" onClick={logout}>
            Sair
          </button>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="studio__error">
          {error}
          <button className="studio__error-close" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="studio__main">
        {/* Sidebar Tree */}
        <aside className="studio__sidebar" style={{ width: sidebarWidth }}>
          <div className="studio__sidebar-header">
            <input
              className="studio__search"
              type="text"
              placeholder="🔍 Buscar..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              id="tree-search"
            />
          </div>
          <div className="studio__tree">
            {treeLoading ? (
              <div className="landing__loading" style={{ padding: '24px' }}>
                <div className="landing__spinner" />
                Carregando...
              </div>
            ) : (
              <TreeView
                nodes={tree}
                filter={searchFilter}
                contentMatchKeys={contentMatchKeys}
                selectedKey={selectedFile?.ikey ?? null}
                onSelect={openFile}
                onCreateFile={(parentKey, parentName) => {
                  setCreateModal({ parentKey, parentName });
                  setCreateFileName('');
                }}
                onDeleteFile={(key, name) => {
                  setDeleteModal({ key, name });
                  setDeleteConfirm('');
                }}
                onRenameFile={async (key, newName) => {
                  try {
                    const result = await renameFile(key, newName);
                    if (selectedFile?.ikey === key) {
                      setSelectedFile({ ...selectedFile, name: result.name });
                      setEditorContent(result.content);
                      editorContentRef.current = result.content;
                      setEditorVersion(v => v + 1);
                    }
                    await loadTree();
                  } catch (err) {
                    setError(`Erro ao renomear: ${err instanceof Error ? err.message : err}`);
                  }
                }}
                onMoveFile={async (fileKey, targetParentKey) => {
                  try {
                    await moveFile(fileKey, targetParentKey);
                    await loadTree();
                  } catch (err) {
                    setError(`Erro ao mover: ${err instanceof Error ? err.message : err}`);
                  }
                }}
              />
            )}
          </div>
        </aside>

        {/* Resize Handle: Sidebar ↔ Editor */}
        <div
          className="studio__resize-handle"
          onMouseDown={(e) => startDrag('sidebar', e)}
        />

        {/* Editor Panel */}
        <main className="studio__editor-panel">
          {selectedFile && (
            <div className="studio__editor-header">
              <div className="studio__editor-path">
                {selectedFile.path.map((p, i) => (
                  <span key={i}>
                    {i > 0 && <span className="studio__editor-path-sep"> / </span>}
                    <span className={i === selectedFile.path.length - 1 ? 'studio__editor-path-current' : ''}>
                      {p}
                    </span>
                  </span>
                ))}
              </div>
              <div className="studio__editor-actions">
                <button
                  className="studio__history-btn"
                  title="Histórico de alterações"
                  onClick={async () => {
                    if (historyOpen) { setHistoryOpen(false); return; }
                    setHistoryLoading(true);
                    setHistoryOpen(true);
                    try {
                      const res = await fetchHistory(selectedFile!.ikey);
                      setHistoryEntries(res.entries);
                    } catch { setHistoryEntries([]); }
                    finally { setHistoryLoading(false); }
                  }}
                >
                  🕐
                </button>
                <span
                  className={`studio__editor-status studio__editor-status--${saveStatus}`}
                  title={saveStatus === 'readonly' && selectedFile?.readOnlyReason ? selectedFile.readOnlyReason : undefined}
                >
                  {saveStatus === 'readonly' && selectedFile?.readOnlyReason
                    ? `🔒 ${selectedFile.readOnlyReason}`
                    : statusLabel[saveStatus]}
                </span>
              </div>
            </div>
          )}

          {historyOpen && selectedFile && (
            <div className="studio__history-panel">
              <div className="studio__history-header">
                <span>🕐 Histórico de edições</span>
                <button className="studio__history-close" onClick={() => setHistoryOpen(false)}>✕</button>
              </div>
              <div className="studio__history-list">
                {historyLoading ? (
                  <div className="studio__history-loading">Carregando...</div>
                ) : historyEntries.length === 0 ? (
                  <div className="studio__history-empty">Nenhuma edição de conteúdo encontrada</div>
                ) : (
                  historyEntries.map((entry, i) => (
                    <div key={i} className="studio__history-entry">
                      <div className="studio__history-meta">
                        <span className="studio__history-date">{entry.date} {entry.time}</span>
                        <span className="studio__history-user">{entry.user}</span>
                      </div>
                      <div className="studio__history-diff">
                        {entry.diff.removed.filter(l => l.trim()).map((line, j) => (
                          <div key={`r${j}`} className="studio__diff-line studio__diff-line--removed">- {line}</div>
                        ))}
                        {entry.diff.added.filter(l => l.trim()).map((line, j) => (
                          <div key={`a${j}`} className="studio__diff-line studio__diff-line--added">+ {line}</div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="studio__editor-content" style={{ display: selectedFile ? 'flex' : 'none', flexDirection: 'column', flex: 1 }}>
            <MarkdownEditor
              content={editorContent}
              onChange={handleContentChange}
              readOnly={saveStatus === 'readonly'}
              version={editorVersion}
            />
          </div>

          {!selectedFile && (
            <div className="studio__editor-empty">
              <div className="studio__editor-empty-icon">📝</div>
              <div className="studio__editor-empty-text">Selecione uma skill na árvore</div>
              <div className="studio__editor-empty-hint">Clique em um arquivo .md na árvore</div>
            </div>
          )}
        </main>

        {/* Resize Handle: Editor ↔ Builder */}
        <div
          className="studio__resize-handle"
          onMouseDown={(e) => startDrag('builder', e)}
        />

        {/* Skill Builder */}
        <aside
          className={`studio__builder ${dragOver ? 'studio__builder--drag-over' : ''}`}
          style={{ width: builderWidth }}
          onDragOver={handleBuilderDragOver}
          onDragLeave={handleBuilderDragLeave}
          onDrop={handleBuilderDrop}
        >
          <div className="studio__builder-header">
            <span className="studio__builder-title-icon">✨</span>
            <span className="studio__builder-title">Skill Builder</span>
          </div>

          {/* Drag overlay */}
          {dragOver && (
            <div className="studio__builder-drag-overlay">
              <div className="studio__builder-drag-overlay-content">
                <span className="studio__builder-drag-overlay-icon">📎</span>
                <span>Solte imagens ou .eml aqui</span>
              </div>
            </div>
          )}

          <div className="studio__builder-messages">
            {chatMessages.length === 0 ? (
              <div className="studio__builder-welcome">
                <div className="studio__builder-welcome-icon">🤖</div>
                <p>Assistente de Edição</p>
                <p style={{ fontSize: '0.78rem', marginTop: 4, color: '#78716C' }}>
                  {selectedFile
                    ? `Editando: ${selectedFile.name}`
                    : 'Selecione um arquivo na árvore para começar'}
                </p>
                <p style={{ fontSize: '0.72rem', marginTop: 8, color: '#9CA3AF' }}>
                  Posso editar, melhorar e reestruturar o conteúdo do arquivo aberto.
                  Para criar, renomear ou deletar arquivos, use os botões na árvore.
                </p>
                <p style={{ fontSize: '0.68rem', marginTop: 6, color: '#9CA3AF', opacity: 0.7 }}>
                  📎 Cole imagens (Ctrl+V), arraste screenshots ou faça upload de .eml
                </p>
              </div>
            ) : (
              chatMessages.map((msg, i) => (
                <div key={i} className={`builder-msg builder-msg--${msg.role}`}>
                  {msg.attachmentTags && msg.attachmentTags.length > 0 && (
                    <div className="builder-msg__attachments">
                      {msg.attachmentTags.map((tag, j) => (
                        <span key={j} className="builder-msg__attachment-tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  {msg.text}
                  {msg.changes?.map((change, j) => (
                    <div key={j} className="builder-diff">
                      <div className="builder-diff__line--remove">{change.before}</div>
                      <div className="builder-diff__line--add">{change.after}</div>
                      <div className="builder-diff__actions">
                        <button
                          className="builder-diff__btn builder-diff__btn--accept"
                          onClick={() => acceptChange(change)}
                        >
                          Aceitar
                        </button>
                        <button className="builder-diff__btn builder-diff__btn--ignore">
                          Ignorar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
            {chatLoading && (
              <div className="builder-msg builder-msg--ai">
                <div className="landing__spinner" style={{ display: 'inline-block', width: 14, height: 14 }} />
                {' '}Pensando...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* File previews */}
          {pendingFiles.length > 0 && (
            <div className="studio__builder-files">
              {pendingFiles.map((file, i) => (
                <div key={i} className="studio__builder-file-chip">
                  <span className="studio__builder-file-icon">
                    {file.type.startsWith('image/') ? '🖼️' : '📧'}
                  </span>
                  <span className="studio__builder-file-name" title={file.name}>
                    {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
                  </span>
                  <span className="studio__builder-file-size">
                    {(file.size / 1024).toFixed(0)}KB
                  </span>
                  <button
                    className="studio__builder-file-remove"
                    onClick={() => removeFile(i)}
                    title="Remover"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="studio__builder-input">
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.webp,.gif,.eml"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            <textarea
              className="studio__builder-textarea"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKey}
              onPaste={handleFilePaste}
              placeholder={selectedFile ? 'Ex: Sugere alguma edição? (Cole imagens com Ctrl+V)' : 'Selecione um arquivo para editar'}
              rows={2}
              id="builder-input"
              disabled={!selectedFile}
            />
            <div className="studio__builder-input-actions">
              <button
                className="studio__builder-attach"
                onClick={() => fileInputRef.current?.click()}
                disabled={!selectedFile}
                title="Anexar imagem ou .eml"
              >
                📎
              </button>
              <button
                className="studio__builder-send"
                onClick={sendChatMessage}
                disabled={chatLoading || (!chatInput.trim() && pendingFiles.length === 0)}
              >
                Enviar
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Create File Modal */}
      {createModal && (
        <div className="studio__modal-overlay" onClick={() => setCreateModal(null)}>
          <div className="studio__modal" onClick={e => e.stopPropagation()}>
            <h3>Criar arquivo em {createModal.parentName}</h3>
            <input
              className="studio__modal-input"
              type="text"
              placeholder="nome-do-arquivo.md"
              value={createFileName}
              onChange={e => setCreateFileName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateFile()}
              autoFocus
            />
            <div className="studio__modal-actions">
              <button className="studio__modal-btn studio__modal-btn--cancel" onClick={() => setCreateModal(null)}>Cancelar</button>
              <button
                type="button"
                className="studio__modal-btn studio__modal-btn--confirm"
                onMouseDown={e => e.preventDefault()}
                onClick={handleCreateFile}
                disabled={isSubmitting || !createFileName.trim()}
              >{isSubmitting ? 'Criando...' : 'Criar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete File Modal */}
      {deleteModal && (
        <div className="studio__modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="studio__modal" onClick={e => e.stopPropagation()}>
            <h3>Deletar {deleteModal.name}?</h3>
            <p className="studio__modal-warning">Digite o nome do arquivo para confirmar:</p>
            <input
              className="studio__modal-input"
              type="text"
              placeholder={deleteModal.name}
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleDeleteFile()}
              autoFocus
            />
            <div className="studio__modal-actions">
              <button className="studio__modal-btn studio__modal-btn--cancel" onClick={() => setDeleteModal(null)}>Cancelar</button>
              <button className="studio__modal-btn studio__modal-btn--danger" onClick={handleDeleteFile} disabled={deleteConfirm !== deleteModal.name || isSubmitting}>{isSubmitting ? 'Deletando...' : 'Deletar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tree View Component ──

function TreeView({
  nodes,
  filter,
  contentMatchKeys,
  selectedKey,
  onSelect,
  onCreateFile,
  onDeleteFile,
  onRenameFile,
  onMoveFile,
  depth = 0,
}: {
  nodes: TreeNode[];
  filter: string;
  contentMatchKeys?: Set<number> | null;
  selectedKey: number | null;
  onSelect: (node: TreeNode) => void;
  onCreateFile?: (parentKey: number, parentName: string) => void;
  onDeleteFile?: (key: number, name: string) => void;
  onRenameFile?: (key: number, newName: string) => Promise<void>;
  onMoveFile?: (fileKey: number, targetParentKey: number) => Promise<void>;
  depth?: number;
}) {
  const filterLower = filter.toLowerCase();

  const matchesFilter = (node: TreeNode): boolean => {
    if (!filterLower) return true;
    if (node.name.toLowerCase().includes(filterLower)) return true;
    // Content match: file key is in the set of matching keys
    if (contentMatchKeys?.has(node.key)) return true;
    return node.children?.some(matchesFilter) ?? false;
  };

  return (
    <>
      {nodes.filter(matchesFilter).map((node) => (
        <TreeNodeItem
          key={node.key}
          node={node}
          depth={depth}
          filter={filterLower}
          contentMatchKeys={contentMatchKeys}
          selectedKey={selectedKey}
          onSelect={onSelect}
          onCreateFile={onCreateFile}
          onDeleteFile={onDeleteFile}
          onRenameFile={onRenameFile}
          onMoveFile={onMoveFile}
        />
      ))}
    </>
  );
}

function TreeNodeItem({
  node,
  depth,
  filter,
  contentMatchKeys,
  selectedKey,
  onSelect,
  onCreateFile,
  onDeleteFile,
  onRenameFile,
  onMoveFile,
}: {
  node: TreeNode;
  depth: number;
  filter: string;
  contentMatchKeys?: Set<number> | null;
  selectedKey: number | null;
  onSelect: (node: TreeNode) => void;
  onCreateFile?: (parentKey: number, parentName: string) => void;
  onDeleteFile?: (key: number, name: string) => void;
  onRenameFile?: (key: number, newName: string) => Promise<void>;
  onMoveFile?: (fileKey: number, targetParentKey: number) => Promise<void>;
}) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const shouldAutoExpand = node.type === 'classe';

  const [expanded, setExpanded] = useState(shouldAutoExpand);

  const icon = getNodeIcon(node);
  const isSelected = node.key === selectedKey;
  const isFile = node.type === 'file';

  // ADR-070: a skill nasce direto na classe da área
  const canAddFile = node.editable === true && node.type === 'classe';
  const canDelete = isFile && node.key > 0 && node.editable;
  const canRename = isFile && node.key > 0 && node.editable;
  const canDrag = isFile && node.key > 0 && node.editable;
  const isDropTarget = node.editable === true && node.type === 'classe';
  const isReadonly = !node.editable && isFile;

  const [dragOver, setDragOver] = useState(false);

  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const baseName = node.name.replace(/\.md$/i, '');

  useEffect(() => {
    if (isRenaming) renameInputRef.current?.focus();
  }, [isRenaming]);

  const submitRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === baseName || renameLoading) {
      setIsRenaming(false);
      setRenameValue(baseName);
      return;
    }
    setRenameLoading(true);
    try {
      await onRenameFile?.(node.key, trimmed);
    } finally {
      setRenameLoading(false);
      setIsRenaming(false);
    }
  };

  const handleClick = () => {
    if (isFile) {
      onSelect(node);
    } else if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  return (
    <div className="tree-node">
      <div
        className={`tree-node__row ${isSelected ? 'tree-node__row--selected' : ''} ${isReadonly ? 'tree-node__row--readonly' : ''} ${dragOver ? 'tree-node__row--drop-target' : ''}`}
        style={{ '--depth': depth } as React.CSSProperties}
        onClick={handleClick}
        draggable={canDrag}
        onDragStart={(e) => {
          e.dataTransfer.setData('flux/file-key', String(node.key));
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragOver={(e) => {
          if (!isDropTarget) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setDragOver(false);
          const fileKey = Number(e.dataTransfer.getData('flux/file-key'));
          if (fileKey && fileKey !== node.key) {
            await onMoveFile?.(fileKey, node.key);
          }
        }}
      >
        <span
          className={`tree-node__chevron ${expanded ? 'tree-node__chevron--open' : ''} ${!hasChildren ? 'tree-node__chevron--hidden' : ''}`}
        >
          ▶
        </span>
        <span className="tree-node__icon">{icon}</span>
        {isRenaming ? (
          <input
            ref={renameInputRef}
            className="tree-node__rename-input"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submitRename();
              if (e.key === 'Escape') { setIsRenaming(false); setRenameValue(baseName); }
              e.stopPropagation();
            }}
            onBlur={submitRename}
            onClick={e => e.stopPropagation()}
            disabled={renameLoading}
          />
        ) : (
          <span
            className={`tree-node__label ${isFile ? 'tree-node__label--file' : ''}`}
          >
            {node.displayName ?? node.name}
            {isReadonly && <span className="tree-node__lock" title="Somente leitura (grupo superior)">🔒</span>}
            {contentMatchKeys?.has(node.key) && <span className="tree-node__content-match" title="Conteúdo corresponde à busca">🔍</span>}
          </span>
        )}
        <span className="tree-node__actions">
          {canAddFile && onCreateFile && (
            <button
              className="tree-node__action-btn"
              title="Criar arquivo .md"
              onClick={(e) => { e.stopPropagation(); onCreateFile(node.key, node.name); }}
            >
              +
            </button>
          )}
          {canRename && onRenameFile && (
            <button
              className="tree-node__action-btn"
              title="Renomear arquivo"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const file = await fetchFile(node.key);
                  const fmMatch = file.content.match(/^---\n[\s\S]*?name:\s*(.+)\n/);
                  const fmName = fmMatch ? fmMatch[1].trim() : baseName;
                  setRenameValue(fmName);
                } catch {
                  setRenameValue(baseName);
                }
                setIsRenaming(true);
              }}
            >
              ✏️
            </button>
          )}
          {canDelete && onDeleteFile && (
            <button
              className="tree-node__action-btn tree-node__action-btn--danger"
              title="Deletar arquivo"
              onClick={(e) => { e.stopPropagation(); onDeleteFile(node.key, node.name); }}
            >
              🗑
            </button>
          )}
        </span>
      </div>
      {expanded && hasChildren && (
        <TreeView
          nodes={node.children!}
          filter={filter}
          contentMatchKeys={contentMatchKeys}
          selectedKey={selectedKey}
          onSelect={onSelect}
          onCreateFile={onCreateFile}
          onDeleteFile={onDeleteFile}
          onRenameFile={onRenameFile}
          onMoveFile={onMoveFile}
          depth={depth + 1}
        />
      )}
    </div>
  );
}

function getNodeIcon(node: TreeNode): string {
  switch (node.type) {
    case 'classe':
      return '📁';
    case 'file':
      return '📄';
    default:
      return '📁';
  }
}

